import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { ThemeProvider } from 'next-themes'
import { TooltipProvider } from './components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { ClerkProvider, SignIn, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react'
import { App } from './App'
import { setTokenGetter } from './lib/auth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The WebSocket is the freshness mechanism; polling on top of it would
      // just duplicate work.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined

/**
 * AuthBridge points the non-React API and WebSocket clients at Clerk's token,
 * and holds the app back until Clerk has finished loading.
 *
 * Rendering the app before `isLoaded` would fire the first /me request with no
 * credential, get a 401, and flash the signed-out screen at a user who is in
 * fact signed in — the classic Clerk hydration flicker.
 */
function AuthBridge({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded } = useAuth()

  useEffect(() => {
    setTokenGetter(() => getToken())
  }, [getToken])

  if (!isLoaded) {
    return (
      <div className="flex h-[100dvh] items-center justify-center">
        <div className="size-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
      </div>
    )
  }
  return <>{children}</>
}

/** SignInScreen is the whole UI when signed out. */
function SignInScreen() {
  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center gap-6 p-6">
      <div className="text-center">
        <p className="text-2xl font-bold">Parley</p>
        <p className="text-muted-foreground">A messenger for your agents.</p>
      </div>
      <SignIn routing="hash" />
    </div>
  )
}

/** MissingKey explains a build that was never given a Clerk publishable key,
 *  which otherwise fails as an opaque crash inside ClerkProvider. */
function MissingKey() {
  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center gap-2 p-8">
      <p className="text-lg font-bold">Clerk is not configured</p>
      <p className="max-w-md text-center text-muted-foreground">
        This build has no <code>VITE_CLERK_PUBLISHABLE_KEY</code>. Set it in the environment for the
        web build (the Dockerfile passes it as a build argument) and rebuild.
      </p>
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <TooltipProvider>
        {!publishableKey ? (
          <MissingKey />
        ) : (
          <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <AuthBridge>
                  <SignedIn>
                    <App />
                  </SignedIn>
                  <SignedOut>
                    <SignInScreen />
                  </SignedOut>
                </AuthBridge>
              </BrowserRouter>
            </QueryClientProvider>
          </ClerkProvider>
        )}
      </TooltipProvider>
    </ThemeProvider>
  </React.StrictMode>,
)
